// Spectator mode — open the URL and watch an AI agent play in 3D first-person
// (the Backrooms experience). No input drives the game: we poll a session's
// live view and drive camera, doors, anomalies, dialogue, voices, sound and
// dread to match. Default target is the resident demo agent (/sauna/featured);
// ?watch=<id> follows a specific agent. Drag to look around.

import THREE from "./three.js";
import type { PerspectiveCamera, Scene, WebGLRenderer, Group, PointLight } from "three";
import type { Hud } from "./hud.js";
import type { World } from "./world/layout.js";
import { buildArchetype, type Archetype } from "./entities/archetypes.js";
import { GameAudio } from "./audio.js";
import { speak, type VoiceSpec } from "./voice.js";

type Stage = "approaching" | "watching" | "present" | "withdrawing";
type EffectKind = "invert" | "slow" | "hallucinate" | "voices" | "dark";

interface SView {
  callsign: string;
  status: "alive" | "dead" | "won";
  room: "reception" | "rest" | "dry_sauna" | "steam_sauna";
  seated: boolean;
  clock: string;
  objective: string;
  dread: number;
  door_state: { dry: boolean; steam: boolean; exit: boolean };
  shower_on: boolean;
  stage?: Stage | null;
  effects?: EffectKind[];
  entity:
    | { id: string; kind: Archetype; says: string; voice: VoiceSpec; stage?: Stage; effect?: EffectKind | null }
    | null;
  death_reason?: string;
  tick: number;
  last_action: string;
  last: string;
  agent_said: string | null;
  agent_reason: string | null;
}

// action verb → an icon + a present-tense label for the activity feed.
const ACT_LABEL: Record<string, [string, string]> = {
  enter: ["🚪", "steps inside"],
  look: ["👁", "looks around"],
  walk: ["🚶", "walks"],
  go: ["🚶", "walks"],
  open: ["🔓", "opens a door"],
  close: ["🔒", "closes a door"],
  sit: ["🪑", "sits down"],
  stand: ["🧍", "stands up"],
  wait: ["⏳", "waits"],
  watch: ["⌚", "checks the watch"],
  read_watch: ["⌚", "checks the watch"],
  shower: ["🚿", "uses the cold shower"],
  reply: ["💬", "answers it"],
  say: ["💬", "answers it"],
  leave: ["🚪", "walks out"],
};
const ROOM_LABEL: Record<string, string> = {
  reception: "reception", rest: "the rest area", dry_sauna: "the dry sauna", steam_sauna: "the steam sauna",
};

// Interior bounds per room (footprint 10×10, with a margin off each wall) so a
// placed presence is clamped into the agent's actual room — never through a wall.
const ROOM_BOUNDS: Record<string, { minX: number; maxX: number; minZ: number; maxZ: number }> = {
  dry_sauna: { minX: 0.5, maxX: 4.5, minZ: 0.5, maxZ: 3.6 },
  steam_sauna: { minX: 5.5, maxX: 9.5, minZ: 0.5, maxZ: 3.6 },
  rest: { minX: 0.5, maxX: 9.5, minZ: 4.4, maxZ: 6.6 },
  reception: { minX: 0.5, maxX: 9.5, minZ: 7.4, maxZ: 9.5 },
};

const FLATLINES = [
  "The benches stayed warm a while.",
  "Steam closed over the place like nothing had happened.",
  "Nobody came to the next session.",
];

export class Spectator {
  private curPos = new THREE.Vector3(5, 1.6, 5.5);
  private curYaw = 0;
  private targetPos = new THREE.Vector3(5, 1.6, 5.5);
  private targetYaw = 0;

  private watchId = "";
  private fixed: string | null;
  private view: SView | null = null;
  private lastTick = -1;
  private entityId = "";
  private entityUpdate: ((t: number, dread: number) => void) | null = null;
  private entityGroup: Group | null = null;
  private entityHead = new THREE.Vector3();
  private entityTarget = new THREE.Vector3();
  private entityStage: Stage = "present";
  private effects: EffectKind[] = [];
  private lookSnap = false;
  private lastSpoken = "";
  private feedEntityId = "";
  private feedEntityLine = "";
  private feedObjective = "";
  private ended = false;
  private flickerBase = new WeakMap<PointLight, number>();
  private audio = new GameAudio();

  // Free-look
  private userYaw = 0;
  private userPitch = 0;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;

  constructor(
    private renderer: WebGLRenderer,
    private scene: Scene,
    private camera: PerspectiveCamera,
    private world: World,
    private hud: Hud,
    fixedWatch: string | null,
  ) {
    this.fixed = fixedWatch;
    this.camera.rotation.order = "YXZ";
  }

  async start() {
    this.makeBadge();
    this.bindLook();
    this.audio.armOnFirstGesture();
    this.hud.hideScreen("screen-start");
    this.hud.setCrosshair(false);
    this.hud.showFeed();
    if (this.fixed) this.watchId = this.fixed;
    else await this.attachFeatured();
    this.poll();
    requestAnimationFrame((t) => this.loop(t));
  }

  // ── Polling ──
  private async attachFeatured() {
    for (let i = 0; i < 100; i++) {
      try {
        const r = await fetch("/sauna/featured").then((x) => x.json());
        if (r.watch_id) { this.watchId = r.watch_id; return; }
      } catch { /* not ready */ }
      await sleep(1000);
    }
  }
  private poll() {
    const tick = async () => {
      if (this.watchId) {
        try {
          const r = await fetch(`/sauna/spectate/${this.watchId}`);
          if (r.ok) this.applyView((await r.json()).view as SView);
          else if (!this.fixed) await this.attachFeatured();
        } catch { /* transient */ }
      }
      setTimeout(tick, 850);
    };
    tick();
  }

  private applyView(v: SView) {
    const fresh = v.tick !== this.lastTick;
    this.lastTick = v.tick;
    this.view = v;

    this.setPose(v);
    this.syncDoor("dry", v.door_state.dry);
    this.syncDoor("steam", v.door_state.steam);
    this.syncDoor("exit", v.door_state.exit);
    this.world.shower.setOn(v.shower_on);
    this.syncEntity(v);

    this.audio.setDread(v.dread);

    const pct = Math.round(v.dread * 100);
    this.hud.setObjective(
      `<span style="color:#c9a14a">👁 ${esc(v.callsign)}</span> &nbsp;·&nbsp; ${esc(v.objective)} ` +
        `&nbsp;·&nbsp; <span class="timer">${v.clock}</span> &nbsp;·&nbsp; dread ${pct}%`,
    );
    this.hud.setDread(v.dread);

    if (fresh) {
      // Only log meaningful beats: any real action, OR a presence appearing /
      // changing its line / the objective advancing / a death-or-win. A plain
      // repeated `wait` with nothing new is skipped so the feed doesn't flood.
      const entId = v.entity ? v.entity.id : "";
      const entLine = v.entity ? v.entity.says : "";
      const appeared = !!v.entity && entId !== this.feedEntityId;
      const left = !v.entity && !!this.feedEntityId;
      const lineChanged = entLine !== this.feedEntityLine;
      const objChanged = v.objective !== this.feedObjective;
      const meaningful =
        v.last_action !== "wait" || appeared || left || lineChanged || objChanged || v.status !== "alive";
      if (meaningful) this.pushFeed(v, appeared, left);
      this.feedEntityId = entId;
      this.feedEntityLine = entLine;
      this.feedObjective = v.objective;
    }

    if (v.status === "dead" && !this.ended) {
      this.ended = true;
      this.audio.deathStinger();
      if (v.death_reason && /look/i.test(v.death_reason) && this.entityGroup) this.lookSnap = true;
      this.hud.redFlash();
      setTimeout(() => {
        this.hud.showDead(v.death_reason ?? "It got you.", FLATLINES[v.tick % FLATLINES.length]);
        this.scheduleNext();
      }, 700);
    } else if (v.status === "won" && !this.ended) {
      this.ended = true;
      this.hud.showWin(`${v.callsign} left at ${v.clock}.`);
      this.scheduleNext();
    }
  }

  // Build one activity row from the fresh view: what the agent did, what it was
  // thinking (reason), what it said, and what the presence said back.
  private pushFeed(v: SView, appeared = false, left = false) {
    // A presence arriving or withdrawing gets its own stark marker row.
    if (appeared && v.entity) {
      this.hud.pushFeed(`<div class="feedrow evil"><div class="act"><span class="ic">⚠</span><span class="t" style="color:#d99">a presence is here</span> &nbsp;<span style="opacity:.6">${v.clock}</span></div><div class="say2">it says: ${esc(v.entity.says)}</div></div>`);
      return;
    }
    if (left) {
      this.hud.pushFeed(`<div class="feedrow"><div class="act"><span class="ic">…</span><span class="t" style="opacity:.8">the presence is gone</span> &nbsp;<span style="opacity:.6">${v.clock}</span></div></div>`);
      return;
    }

    const [ic, baseLabel] = ACT_LABEL[v.last_action] ?? ["•", v.last_action];
    let label = baseLabel;
    if (v.last_action === "walk" || v.last_action === "go") label = `walks into ${ROOM_LABEL[v.room] ?? v.room}`;
    if (v.last_action === "leave" && v.status === "won") label = "steps out into the night";
    if (v.status === "dead") label = "breaks the rule";

    const parts: string[] = [];
    const ic2 = v.status === "dead" ? "💀" : ic;
    parts.push(`<div class="act"><span class="ic">${ic2}</span><span class="t">${esc(label)}</span> &nbsp;<span style="opacity:.6">${v.clock}</span></div>`);
    if (v.agent_reason) parts.push(`<div class="think">${esc(v.agent_reason)}</div>`);
    if ((v.last_action === "reply" || v.last_action === "say") && v.agent_said) parts.push(`<div class="said">${esc(v.agent_said)}</div>`);
    const evil = !!v.entity || v.status === "dead";
    if (v.entity) parts.push(`<div class="say2">it says: ${esc(v.entity.says)}</div>`);

    this.hud.pushFeed(`<div class="feedrow${evil ? " evil" : ""}">${parts.join("")}</div>`);
  }

  private scheduleNext() {
    if (this.fixed) return;
    const finished = this.watchId;
    const tryNext = async () => {
      try {
        const r = await fetch("/sauna/featured").then((x) => x.json());
        if (r.watch_id && r.watch_id !== finished) { this.resetForNext(r.watch_id); return; }
      } catch { /* retry */ }
      setTimeout(tryNext, 1500);
    };
    setTimeout(tryNext, 5000);
  }
  private resetForNext(newWatch: string) {
    this.hud.hideScreen("screen-dead");
    this.hud.hideScreen("screen-win");
    this.hud.hideDialogue();
    this.hud.clearFeed();
    this.clearEntity();
    for (const k of ["dry", "steam", "exit"] as const) if (this.world.doors[k].isOpen) this.world.doors[k].toggle();
    this.world.shower.setOn(false);
    this.curPos.set(5, 1.6, 5.5);
    this.curYaw = 0;
    this.lastTick = -1;
    this.ended = false;
    this.lookSnap = false;
    this.lastSpoken = "";
    this.feedEntityId = "";
    this.feedEntityLine = "";
    this.feedObjective = "";
    this.watchId = newWatch;
  }

  // ── Scene sync ──
  private syncDoor(k: "dry" | "steam" | "exit", open: boolean) {
    if (this.world.doors[k].isOpen !== open) this.world.doors[k].toggle();
  }

  private syncEntity(v: SView) {
    const e = v.entity;
    const id = e ? e.id : "";
    const stage: Stage = (e?.stage ?? v.stage ?? "present") as Stage;
    this.entityStage = stage;
    this.effects = e ? (v.effects ?? []) : [];

    if (id !== this.entityId) {
      this.clearEntity();
      this.entityId = id;
      if (e) {
        const avatar = buildArchetype(e.kind);
        const place = this.placeEntity(v, e.kind, stage);
        // Spawn it AT its staged position (far off / at the window) and let the
        // loop lerp it inward as the stage advances — it walks in, never pops in.
        avatar.group.position.copy(place.pos);
        avatar.group.rotation.y = place.yaw;
        this.scene.add(avatar.group);
        this.entityGroup = avatar.group;
        this.entityUpdate = avatar.update;
        this.entityTarget.copy(place.pos);
        this.entityHead.set(place.pos.x, 1.45, place.pos.z);
        this.audio.appearStinger();
      }
    } else if (e && this.entityGroup) {
      // Same entity, new stage → retarget; the loop walks it to the new spot.
      const place = this.placeEntity(v, e.kind, stage);
      this.entityTarget.copy(place.pos);
      this.entityGroup.rotation.y = place.yaw;
      this.entityHead.set(place.pos.x, 1.45, place.pos.z);
    }

    if (e) {
      this.hud.showDialogue(e.says, [], () => {});
      const key = e.id + "|" + e.says;
      if (key !== this.lastSpoken) {
        this.lastSpoken = key;
        speak(e.says, e.voice, stage);
      }
    } else {
      this.hud.hideDialogue();
    }
  }

  private placeEntity(v: SView, kind: Archetype, stage: Stage): { pos: THREE.Vector3; yaw: number } {
    // The window face mounts on the porthole — and recedes into the dark while it
    // is still only approaching, sliding flush to the glass once it's present.
    if (kind === "face" && v.room === "dry_sauna") {
      const a = this.world.windowAnchor.position;
      const z = stage === "approaching" ? -0.9 : stage === "watching" ? -0.4 : -0.1;
      return { pos: new THREE.Vector3(a.x, 0, z), yaw: 0 };
    }
    // Otherwise place it ahead of the agent, facing them — CLAMPED to the room so
    // it never ends up shoved through a wall. It starts FAR (approaching), pauses
    // at the threshold (watching), then closes to conversational range (present).
    const fwd = new THREE.Vector3(-Math.sin(this.targetYaw), 0, -Math.cos(this.targetYaw));
    const right = new THREE.Vector3(Math.cos(this.targetYaw), 0, -Math.sin(this.targetYaw));
    const close = v.seated ? 1.4 : 2.1;
    const d = stage === "approaching" ? close + 3.0 : stage === "watching" ? close + 1.4 : close;
    const pos = new THREE.Vector3()
      .copy(this.targetPos)
      .addScaledVector(fwd, d)
      .addScaledVector(right, v.seated ? 0.7 : 0.2);
    pos.y = 0;
    const b = ROOM_BOUNDS[v.room] ?? ROOM_BOUNDS.rest;
    pos.x = Math.max(b.minX, Math.min(b.maxX, pos.x));
    pos.z = Math.max(b.minZ, Math.min(b.maxZ, pos.z));
    return { pos, yaw: this.targetYaw + Math.PI }; // face back toward the agent
  }

  private clearEntity() {
    if (this.entityGroup) this.scene.remove(this.entityGroup);
    this.entityGroup = null;
    this.entityUpdate = null;
    this.entityId = "";
    this.entityStage = "present";
    this.effects = [];
  }

  // ── Camera posing ──
  private setPose(v: SView) {
    if (v.status === "won") { this.targetPos.set(5, 1.6, 10.8); this.targetYaw = Math.PI; return; }
    if (v.room === "dry_sauna" && v.seated) { this.targetPos.set(3.5, 1.15, 3.5); this.targetYaw = 0; return; }
    if (v.room === "steam_sauna" && v.seated) { this.targetPos.set(6.3, 1.15, 3.5); this.targetYaw = 0; return; }
    switch (v.room) {
      case "rest": this.targetPos.set(5, 1.6, 5.5); this.targetYaw = 0; break;
      case "reception": this.targetPos.set(5, 1.6, 8.4); this.targetYaw = Math.PI; break;
      case "dry_sauna": this.targetPos.set(2.5, 1.6, 2.2); this.targetYaw = 0; break;
      case "steam_sauna": this.targetPos.set(7.5, 1.6, 2.2); this.targetYaw = 0; break;
    }
  }
  private yawTo(t: THREE.Vector3): number {
    return Math.atan2(-(t.x - this.curPos.x), -(t.z - this.curPos.z));
  }

  // ── Free-look ──
  private bindLook() {
    const el = this.renderer.domElement;
    el.style.cursor = "grab";
    const down = (x: number, y: number) => { this.dragging = true; this.lastX = x; this.lastY = y; el.style.cursor = "grabbing"; };
    const move = (x: number, y: number) => {
      if (!this.dragging) return;
      this.userYaw -= (x - this.lastX) * 0.005;
      this.userPitch = Math.max(-1.1, Math.min(1.1, this.userPitch - (y - this.lastY) * 0.005));
      this.userYaw = Math.max(-Math.PI, Math.min(Math.PI, this.userYaw));
      this.lastX = x; this.lastY = y;
    };
    const up = () => { this.dragging = false; el.style.cursor = "grab"; };
    el.addEventListener("mousedown", (e) => down(e.clientX, e.clientY));
    window.addEventListener("mousemove", (e) => move(e.clientX, e.clientY));
    window.addEventListener("mouseup", up);
    el.addEventListener("touchstart", (e) => down(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
    window.addEventListener("touchmove", (e) => e.touches[0] && move(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
    window.addEventListener("touchend", up);
  }

  // ── Render loop ──
  private loop(now: number) {
    const dread = this.view?.dread ?? 0;
    const fx = this.effects;
    const slow = fx.includes("slow");
    const hallu = fx.includes("hallucinate");
    const invert = fx.includes("invert");
    const dark = fx.includes("dark");

    // "slow" drags real time out — the whole view eases as if through syrup.
    const timeScale = slow ? 0.35 : 1;
    const t = (now / 1000) * timeScale;

    this.curPos.lerp(this.targetPos, slow ? 0.02 : 0.06);
    // Walk a staging presence toward its target slot (approaching → present); it
    // closes in instead of teleporting. Keep its feet on the floor.
    if (this.entityGroup) {
      this.entityGroup.position.lerp(this.entityTarget, slow ? 0.03 : 0.05);
      this.entityGroup.position.y = 0;
    }

    let aimYaw = this.targetYaw;
    if (this.lookSnap) aimYaw = this.yawTo(this.entityHead);
    this.curYaw += shortestAngle(this.curYaw, aimYaw) * (this.lookSnap ? 0.2 : 0.08);
    if (!this.dragging) { this.userYaw *= 0.97; this.userPitch *= 0.97; }

    // Effect-driven camera disturbance (only ever while a presence is "present").
    // hallucinate → the room breathes and lists; invert → a sick slow roll.
    let roll = 0;
    let jitter = 0;
    let fovMul = 1;
    if (hallu) {
      roll += Math.sin(now * 0.0013) * 0.05;
      fovMul *= 1 + Math.sin(now * 0.0017) * 0.06;
      jitter = 0.012 * (0.5 + dread);
    }
    if (invert) roll += Math.sin(now * 0.0008) * 0.6; // a nauseous, tilting world
    const jx = jitter ? (Math.sin(now * 0.043) + Math.sin(now * 0.071)) * jitter : 0;
    const jy = jitter ? (Math.sin(now * 0.037) + Math.sin(now * 0.067)) * jitter : 0;

    this.camera.position.set(this.curPos.x + jx, this.curPos.y + jy, this.curPos.z);
    this.camera.rotation.set(this.userPitch, this.curYaw + this.userYaw, roll, "YXZ");
    const baseFov = 72;
    const wantFov = baseFov * fovMul;
    if (Math.abs(this.camera.fov - wantFov) > 0.01) {
      this.camera.fov = wantFov;
      this.camera.updateProjectionMatrix();
    }

    if (this.entityUpdate) this.entityUpdate(t, dread);

    // "dark" drinks the light; otherwise lights just flicker with dread.
    const darkMul = dark ? 0.35 : 1;
    const flick = (1 - dread * (0.3 + 0.4 * Math.abs(Math.sin(now * 0.02)))) * darkMul;
    for (const l of this.world.flickerLights as PointLight[]) {
      let base = this.flickerBase.get(l);
      if (base === undefined) { base = l.intensity; this.flickerBase.set(l, base); }
      l.intensity = base * flick;
    }
    const fog = this.scene.fog as THREE.FogExp2 | null;
    if (fog && "density" in fog) fog.density = 0.1 + (dark ? 0.12 : 0) + dread * 0.04;

    // Whispering at the ear — the "voices" effect drives a breath/whisper bed.
    this.audio.setWhispers(fx.includes("voices"));

    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame((n) => this.loop(n));
  }

  // ── Badge ──
  private makeBadge() {
    const b = document.createElement("div");
    b.innerHTML = `👁 SPECTATING — an AI agent is playing. <b style="color:#c9a14a">click for sound</b> · drag to look · <a href="?play=1" style="color:#c9a14a">play it yourself →</a>`;
    Object.assign(b.style, {
      position: "fixed", left: "12px", bottom: "12px", zIndex: "50",
      font: "12px ui-monospace, monospace", color: "#cfc8b8",
      background: "rgba(0,0,0,0.55)", padding: "6px 10px", borderRadius: "4px",
      borderLeft: "3px solid #c9a14a",
    } as CSSStyleDeclaration);
    document.body.appendChild(b);
  }
}

function shortestAngle(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
function sleep(ms: number) { return new Promise<void>((r) => setTimeout(r, ms)); }
function esc(s: string): string { return s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c] ?? c); }
